import { Injectable, signal } from "@angular/core";
import type {
  BlackboardIndicator,
  BlackboardPhase,
  BlackboardRunAttempt,
  BlackboardRunWait,
  BlackboardSnapshotEvent,
  BlackboardTaskRow,
} from "../model/block.types";
import type { ActionState, EngineRunState } from "../model/engine-run-state.types";

/**
 * Frontend-side per-task attempt metadata distilled from a `blackboard_snapshot` attempt-start
 * event (SSE v2, additive surface) — the executing resolver, the streamed intent text and the
 * 1-based attempt counter of the most recent attempt of each task.
 */
export interface BlackboardTaskAttemptInfo {
  /** Catalog id executing the attempt; null when the event carried none. */
  resolverId: string | null;
  /** `task_description` (Task.intent) of the attempt-start event; null when absent. */
  taskDescription: string | null;
  /** 1-based attempt counter of the attempt-start event (defaults to 1 when absent). */
  attempt: number;
}

/**
 * Frontend-side per-pass engine state distilled from an ENGINE-level `blackboard_snapshot`
 * `run_engine` event (engine-pass contract: `task_id === null`) — one entry per `engine_pass`
 * seen, so per-pass progress is distinguishable instead of collapsing into the single
 * `run_engine` phase slot. `pass` is the raw event `engine_pass` (null = the neutral fallback
 * group for passes without a policy); `status` is the raw lowercase engine-level vocabulary
 * (`"running"`/`"completed"`, tolerated verbatim for unknown future strings).
 */
export interface BlackboardEnginePassState {
  /** Raw `engine_pass` of the event; null = neutral fallback group (pass without a policy). */
  pass: string | null;
  /** Raw lowercase engine-level status of the pass (`"running"`/`"completed"`). */
  status: string;
}

/** Raw ActionState words — tolerated verbatim on task-level events (legacy contract generations). */
const RAW_ACTION_STATES: ReadonlySet<string> = new Set([
  "PENDING",
  "READY",
  "DONE",
  "FAILED",
  "WAITING_USER",
  "WAITING_INPUT",
]);

/**
 * Translates a TASK-level event `status` onto the internal row vocabulary (status contract v3):
 * `completed` → `DONE`, `error` → `FAILED`, `waiting` → the collapsed `"WAITING"` park state,
 * the transient `running` passes through, and the raw ActionState words of older contract
 * generations pass through verbatim. Unknown strings return null — the caller logs and
 * preserves the stored state, never guessing.
 */
function translateTaskLevelStatus(status: string): ActionState | "running" | "WAITING" | null {
  switch (status) {
    case "running":
      return "running";
    case "completed":
      return "DONE";
    case "error":
      return "FAILED";
    case "waiting":
      return "WAITING";
    default:
      return RAW_ACTION_STATES.has(status) ? (status as ActionState) : null;
  }
}

/**
 * Holds the live planning-pipeline progress streamed as `blackboard_snapshot` CUSTOM events
 * (doc 31 §7) — one event per phase of the 8-phase catalog (`plan_router`,
 * `semantic_decomposition`, `capability_resolution`, `provider_binding`, `plan_triage`,
 * `plan_assessment`, `plan_run`, `run_engine`). `chat-container` routes matching events here;
 * `chat-message-list` weaves the current state into the turn as a `PlanProgressCardComponent`,
 * same pattern as `WorkerReasoningStreamService`/`WorkerTodoStreamService`.
 *
 * The plan aggregate never travels by this event (doc 31 §3): it arrives through the `plan`
 * content block and is joined by `plan_id` (§7.5) — that join lives in the plan block store
 * (task T4), so this service only keeps the engine-side live state.
 *
 * SSE v2 (B1) additionally records the LIVE planning-progress list (`tasks` — pre-engine
 * intent labels), the streamed per-task descriptions/resolver ids from attempt-start events,
 * the live attempt line (`runAttempt`) and the park/wait reason (`runWait`) — the raw state
 * the plan progress card's detail line and label fallbacks consume.
 *
 * Provided per chat-container (NOT root) so it resets with the container. Cleared explicitly
 * on submit for new turns, same lifecycle as its sibling stream services.
 */
@Injectable()
export class BlackboardStreamService {
  readonly #indicators = signal<Partial<Record<BlackboardPhase, BlackboardIndicator>>>({});

  readonly #taskRows = signal<Map<string, BlackboardTaskRow>>(new Map());

  readonly #planId = signal<string | null>(null);

  readonly #runError = signal<string | null>(null);

  readonly #engineRunState = signal<EngineRunState | null>(null);

  readonly #planningTasks = signal<ReadonlyMap<string, string>>(new Map());

  readonly #taskDescriptions = signal<ReadonlyMap<string, string>>(new Map());

  readonly #taskResolvers = signal<ReadonlyMap<string, string>>(new Map());

  readonly #runAttempt = signal<BlackboardRunAttempt | null>(null);

  readonly #runWait = signal<BlackboardRunWait | null>(null);

  /** Per-task latest attempt-start metadata (SSE v2), keyed by task_id. Reactive. */
  readonly #taskAttempts = signal<ReadonlyMap<string, BlackboardTaskAttemptInfo>>(new Map());

  /** Per-task pending detail of the latest park event (SSE v2), keyed by task_id. Reactive. */
  readonly #taskPendingDetails = signal<ReadonlyMap<string, string>>(new Map());

  /** Per-pass engine state of the latest engine-level `run_engine` events (engine-pass contract). Reactive. */
  readonly #enginePasses = signal<ReadonlyMap<string, BlackboardEnginePassState>>(new Map());

  /** Live phase indicators, keyed by phase name. Reactive — read in computed()s. */
  readonly indicators = this.#indicators.asReadonly();

  /** Live per-task rows of the engine run, keyed by task_id. Reactive. */
  readonly taskRows = this.#taskRows.asReadonly();

  /** `plan_id` join key from the last full EngineRunState snapshot (doc 31 §7.5). Reactive. */
  readonly planId = this.#planId.asReadonly();

  /** Catastrophic engine-run error from the last full EngineRunState snapshot, or null (doc 31 §8 #8). Reactive. */
  readonly runError = this.#runError.asReadonly();

  /**
   * Last FULL EngineRunState snapshot received (doc 31 §7.4/§8) — debug readout of the run's
   * control fields (turn_id, turn_index, run, session_id, HITL counters). Null until the first
   * full snapshot arrives; intermediate null-snapshot events NEVER clear it (null-keep rule) —
   * only a newer full snapshot replaces it, or `clear()`.
   */
  readonly engineRunState = this.#engineRunState.asReadonly();

  /**
   * LIVE planning-progress intents (SSE v2 `tasks` list), keyed by task_id. Replaced WHOLESALE
   * whenever an event carries a non-null `tasks` (the current revision's list — last wins);
   * a null `tasks` NEVER clears it (the `_generate` running event carries none — null-keep,
   * same rule as `plan_run_state`). Reactive.
   */
  readonly planningTasks = this.#planningTasks.asReadonly();

  /** Streamed task descriptions (SSE v2 attempt-start `task_description`), keyed by task_id. Reactive. */
  readonly taskDescriptions = this.#taskDescriptions.asReadonly();

  /** Streamed resolver ids (SSE v2 attempt-start `resolver_id`), keyed by task_id. Reactive. */
  readonly taskResolvers = this.#taskResolvers.asReadonly();

  /** Live attempt-start line (SSE v2) — null while no attempt is running; heartbeats keep it. Reactive. */
  readonly runAttempt = this.#runAttempt.asReadonly();

  /** Live park/wait reason (SSE v2) — null while not parked; attempt-start and full snapshots clear it. Reactive. */
  readonly runWait = this.#runWait.asReadonly();

  /**
   * Per-task latest attempt-start metadata (SSE v2, B1), keyed by task_id: `{resolverId,
   * taskDescription, attempt}` of the most recent attempt-start event of each task. Map insertion
   * order tracks event recency (updates delete+set) — the globally newest attempt-start is the
   * LAST entry. Kept across full snapshots (label-provenance metadata, same policy as
   * `taskDescriptions`/`taskResolvers`); reset only by `clear()`. Reactive.
   */
  readonly taskAttempts = this.#taskAttempts.asReadonly();

  /**
   * Per-task pending detail (SSE v2, B1), keyed by task_id: the `detail` string of the task's
   * latest park event (`PENDING`/`WAITING_INPUT`/`WAITING_USER` + detail). Map insertion order
   * tracks event recency (updates delete+set) — the globally newest park is the LAST entry.
   * Cleared per task when THAT task attempt-starts again, wholesale by a fresh full snapshot
   * (the same event may re-report its own park right after) or by `clear()`. Reactive.
   */
  readonly taskPendingDetails = this.#taskPendingDetails.asReadonly();

  /**
   * Per-pass engine state (engine-pass contract), keyed by `engine_pass` — the empty string keys
   * the neutral fallback group for events whose `engine_pass` was null or absent (pass without a
   * policy); every entry carries the raw `pass` name (null for the fallback) and the latest
   * lowercase engine-level `status`. Only ENGINE-level `run_engine` events (`task_id === null`)
   * feed this map — task-level events never create or update pass rows. Reactive.
   */
  readonly enginePasses = this.#enginePasses.asReadonly();

  /**
   * Upsert state from a `blackboard_snapshot` event (doc 31 §7.1, SSE v2). Every event overwrites
   * its phase indicator in place, keyed by `event.phase`. For `run_engine`, the event level is
   * discriminated by `task_id` nullity (engine-pass contract): a TASK-level event (`task_id !==
   * null`) carries the raw ActionState of the task whose transition fired the event (or the
   * transient lowercase `"running"` on attempt-start) and upserts that task's row — engine-level
   * lowercase statuses are NEVER cast into ActionState — while an ENGINE-level event
   * (`task_id === null`) carries the lowercase engine vocabulary and updates only the per-pass
   * state of its `engine_pass` (null/absent → the neutral fallback group). A full
   * `plan_run_state` snapshot REPLACES task rows wholesale, refreshes `planId`/`runError` and
   * resets the attempt/wait picture (fresh truth — maps intentionally kept), while an
   * intermediate event (null `plan_run_state`) only upserts the named task's row from `status`,
   * keeping `attempts` at max(stored, event.attempt) and preserving it when the event carries
   * none. A null `plan_run_state` must NEVER clear or shrink stored rows, plan_id or error —
   * last known values are kept until the next full snapshot (doc 31 §7.4 null-keep rule).
   * Attempt-start events also feed `taskDescriptions`/`taskResolvers`, upsert the per-task
   * `taskAttempts` metadata, clear that task's `taskPendingDetails` entry and set `runAttempt`
   * (clearing any `runWait`); park events (`PENDING`/`WAITING_INPUT`/`WAITING_USER` + detail)
   * set `runWait` (success-route states only) and the per-task `taskPendingDetails` entry;
   * a non-null `tasks` list replaces `planningTasks` wholesale (null keeps it). Never throws
   * from the stream path.
   */
  store(event: BlackboardSnapshotEvent): void {
    this.#indicators.update((indicators) => {
      const next = { ...indicators };
      // In-place phase update keyed by `phase` (doc 31 §7.1). SSE v2: plan_router emits its own
      // `completed` on the three success routes (doc 31 §7.2), so every phase closes itself —
      // no implicit-close heuristic is needed (it would erase a genuinely completed Router).
      next[event.phase] = { status: event.status, step: event.step, attempt: event.attempt };
      return next;
    });

    // Contract shape guard: the EngineRunState dump only travels with phase "run_engine"
    // (doc 31 §7.1). Never throw from the stream path — log and skip the payload.
    if (event.plan_run_state != null && event.phase !== "run_engine") {
      console.error("blackboard_snapshot: unexpected plan_run_state on phase", event.phase, event);
      return;
    }

    // LIVE planning-progress list (SSE v2): a non-null `tasks` carries the CURRENT revision's
    // list (F2/F3 completed, review/plan_triage running — during an F1 review it is the current
    // plan's list) → replace the stored map wholesale, last wins. A null `tasks` must NEVER
    // clear the map: the `_generate` running event carries no tasks — the preceding completed
    // already delivered them (null-keep, same rule as plan_run_state). Shape-guarded, never
    // thrown on. List elements carry `{task_id, intent}` (backend `plan_tasks_payload`) —
    // the event-level `task_description` is a DIFFERENT key (attempt-start only).
    if (event.tasks != null) {
      if (Array.isArray(event.tasks)) {
        const planning = new Map<string, string>();
        for (const task of event.tasks) {
          if (
            task != null &&
            typeof task.task_id === "string" &&
            typeof task.intent === "string" &&
            task.intent.length > 0
          ) {
            planning.set(task.task_id, task.intent);
          }
        }
        this.#planningTasks.set(planning);
      } else {
        console.error("blackboard_snapshot: unexpected tasks payload shape", event);
      }
    }

    // Engine-level vs task-level discrimination (engine-pass contract): `task_id` nullity is
    // THE discriminator. An ENGINE-level `run_engine` event (`task_id === null` — tick
    // heartbeat, per-pass progress/completion) carries the lowercase engine vocabulary and
    // updates ONLY the per-pass state of its `engine_pass`; its status must never be cast
    // into ActionState or touch task rows. A null/absent `engine_pass` groups under the
    // neutral fallback entry (pass without a policy — and legacy events before this key
    // existed). Passes never seen stay absent: rows are created by events, never pre-created.
    if (event.phase === "run_engine" && event.task_id == null) {
      const pass = typeof event.engine_pass === "string" && event.engine_pass.length > 0 ? event.engine_pass : null;
      const passKey = pass ?? "";
      this.#enginePasses.update((passes) => {
        const previous = passes.get(passKey);
        if (previous && previous.pass === pass && previous.status === event.status) return passes;
        const next = new Map(passes);
        next.set(passKey, { pass, status: event.status });
        return next;
      });
    }

    if (event.phase === "run_engine" && event.plan_run_state != null) {
      // Full EngineRunState snapshot (terminal task transition or HITL suspension change,
      // doc 31 §7.4): replace task rows wholesale — one row per TaskExecution. This complete
      // snapshot is the fresher view; the named-task upsert below is a no-op for a task the
      // snapshot already carries (the event's `status` is that same transition's ActionState).
      const snapshot = event.plan_run_state;
      const rows = new Map<string, BlackboardTaskRow>();
      for (const task of snapshot.tasks) {
        rows.set(task.task_id, { task_id: task.task_id, state: task.state, attempts: task.attempts.length });
      }
      this.#taskRows.set(rows);
      this.#planId.set(snapshot.plan_id);
      this.#runError.set(snapshot.error);
      this.#engineRunState.set(snapshot);
      // Fresh truth: the full snapshot re-derives the attempt/wait picture — reset both. The
      // description/resolver/planning/attempt maps are intentionally KEPT (they are still the
      // best label sources; the snapshot carries none of them). The per-task pending details
      // reset too — a same-event park re-reports its own detail right below this block.
      this.#runAttempt.set(null);
      this.#runWait.set(null);
      this.#taskPendingDetails.set(new Map());
    }

    const taskId = event.task_id;
    if (event.phase === "run_engine" && taskId != null) {
      // SSE v2 (doc 31 §7.3): a TASK-level event (`task_id !== null`) names the TaskExecution
      // whose state/attempts changed — intermediate transitions carry only `status` +
      // `task_id` while `plan_run_state` is null (doc 31 §7.4). Upsert just that task's row
      // from the TRANSLATED status (status contract v3: `completed`→DONE, `error`→FAILED,
      // `waiting`→collapsed WAITING, transient `running` on attempt-start, legacy raw
      // ActionState words verbatim): update `state` in place and keep `attempts` at
      // max(stored, event.attempt) — intermediate events carry no attempts (the stored count
      // is preserved); an attempt-start carries the 1-based attempt and must never regress
      // below the stored count. Engine-level lowercase statuses never reach this branch
      // (discriminated above by task_id nullity).
      const translated = translateTaskLevelStatus(event.status);
      if (translated === null) {
        // Unknown vocabulary: log and preserve the stored row — never guess a state.
        console.error("blackboard_snapshot: unexpected task-level status", event.status, event);
        return;
      }
      // A collapsed `waiting` never downgrades a raw parked state the same event's full
      // snapshot (fresher, finer word) just stored — see #collapsedWaitingState.
      const state = translated === "WAITING" ? this.#collapsedWaitingState(taskId) : translated;
      this.#taskRows.update((rows) => {
        const next = new Map(rows);
        const stored = next.get(taskId)?.attempts ?? 0;
        const attempts = event.attempt != null ? Math.max(stored, event.attempt) : stored;
        next.set(taskId, { task_id: taskId, state, attempts });
        return next;
      });

      // Attempt-start metadata: the event may carry the task's intent and the catalog id
      // executing the attempt — recorded as label-join fallbacks (missing on other events).
      if (typeof event.task_description === "string" && event.task_description.length > 0) {
        const description = event.task_description;
        this.#taskDescriptions.update((descriptions) => {
          if (descriptions.get(taskId) === description) return descriptions;
          const next = new Map(descriptions);
          next.set(taskId, description);
          return next;
        });
      }
      if (typeof event.resolver_id === "string" && event.resolver_id.length > 0) {
        const resolverId = event.resolver_id;
        this.#taskResolvers.update((resolvers) => {
          if (resolvers.get(taskId) === resolverId) return resolvers;
          const next = new Map(resolvers);
          next.set(taskId, resolverId);
          return next;
        });
      }

      if (state === "running") {
        // Attempt-start (SSE v2): the named task began attempt N via its resolver — remember
        // the live attempt line and clear any parked/waiting note (work resumed). A heartbeat
        // never reaches here (it carries no task_id), so `runAttempt` survives liveness ticks.
        this.#runAttempt.set({
          task_id: taskId,
          description: typeof event.task_description === "string" ? event.task_description : null,
          resolver_id: typeof event.resolver_id === "string" ? event.resolver_id : null,
          attempt: event.attempt ?? 1,
        });
        this.#runWait.set(null);
        // Per-task attempt metadata (SSE v2, B1): the entry is replaced with a delete+set so
        // map insertion order tracks event recency — the newest attempt-start is the LAST entry.
        const attemptInfo: BlackboardTaskAttemptInfo = {
          taskDescription: typeof event.task_description === "string" ? event.task_description : null,
          resolverId: typeof event.resolver_id === "string" ? event.resolver_id : null,
          attempt: event.attempt ?? 1,
        };
        this.#taskAttempts.update((attempts) => {
          const previous = attempts.get(taskId);
          if (
            previous &&
            previous.taskDescription === attemptInfo.taskDescription &&
            previous.resolverId === attemptInfo.resolverId &&
            previous.attempt === attemptInfo.attempt
          ) {
            return attempts;
          }
          const next = new Map(attempts);
          next.delete(taskId);
          next.set(taskId, attemptInfo);
          return next;
        });
        // This task's pending detail goes stale the moment it starts working again — peers
        // that are still parked keep their detail (per-task precision, unlike `runWait`).
        this.#taskPendingDetails.update((details) => {
          if (!details.has(taskId)) return details;
          const next = new Map(details);
          next.delete(taskId);
          return next;
        });
      } else if (
        (state === "PENDING" || state === "WAITING_INPUT" || state === "WAITING") &&
        typeof event.detail === "string" &&
        event.detail.length > 0
      ) {
        // Park/blocked (SSE v2 + status contract v3): `PENDING`+detail · `WAITING_INPUT`+detail
        // · collapsed `waiting`+detail — the reason surfaces in the card's detail line until
        // the next attempt-start or full snapshot.
        this.#runWait.set({ state, detail: event.detail });
      }
      // Per-task pending detail (SSE v2, B1): any parked state (`PENDING`/`WAITING_INPUT`/
      // `WAITING_USER`/collapsed `WAITING`) with a detail string records the reason per
      // task_id. Same delete+set recency rule as `taskAttempts`; cleared by this task's next
      // attempt-start, a fresh full snapshot or `clear()`.
      if (
        (state === "PENDING" || state === "WAITING_INPUT" || state === "WAITING_USER" || state === "WAITING") &&
        typeof event.detail === "string" &&
        event.detail.length > 0
      ) {
        const detail = event.detail;
        this.#taskPendingDetails.update((details) => {
          if (details.get(taskId) === detail) return details;
          const next = new Map(details);
          next.delete(taskId);
          next.set(taskId, detail);
          return next;
        });
      }
    }
    // run_engine with plan_run_state === null and no named task (tick heartbeat, pure
    // liveness): the indicator upsert and the per-pass state update above are the only
    // updates; stored rows, plan_id, error, maps and the live attempt line are intentionally
    // kept (doc 31 §7.4 null-keep rule).
  }

  /**
   * Drop all indicators, task rows, plan_id, run error, the last full EngineRunState snapshot,
   * the planning/description/resolver/attempt/pending-detail maps, the per-pass engine state
   * and the live attempt/wait picture. Call on submit so a new turn starts clean.
   */
  clear(): void {
    this.#indicators.set({});
    this.#taskRows.set(new Map());
    this.#planId.set(null);
    this.#runError.set(null);
    this.#engineRunState.set(null);
    this.#planningTasks.set(new Map());
    this.#taskDescriptions.set(new Map());
    this.#taskResolvers.set(new Map());
    this.#taskAttempts.set(new Map());
    this.#taskPendingDetails.set(new Map());
    this.#enginePasses.set(new Map());
    this.#runAttempt.set(null);
    this.#runWait.set(null);
  }

  /**
   * Resolves the collapsed `WAITING` against the task's stored row (status contract v3): a
   * task a full snapshot already carries in a raw parked state keeps that variant (the dump
   * is the fresher truth and carries the finer word); otherwise the collapsed state stands.
   */
  #collapsedWaitingState(taskId: string): ActionState | "WAITING" {
    const parked = this.#taskRows().get(taskId)?.state;
    if (parked === "PENDING" || parked === "WAITING_INPUT" || parked === "WAITING_USER") {
      return parked;
    }
    return "WAITING";
  }
}
